/**
 * @file SuperAdminDashboardPage.tsx
 * @brief Landing page for super admins — platform KPI overview + trend charts.
 *
 * @details
 * Surfaces all-time user/company totals plus today’s sessions and
 * month-to-date revenue, with 30-day session/revenue/growth charts from
 * existing `/api/admin/analytics/*` endpoints. Layout uses GridBlock
 * section cells (hairline dividers) flush to the content rail.
 *
 * @references
 * - BACKEND/src/features/admin/routes.js (analytics endpoints)
 * - WEBPANEL/src/features/analytics/api.ts
 *
 * @author DES
 * @date 26-05-2026
 * @updated 29-07-2026 — section grid layout
 */

import { useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Activity,
  ArrowRight,
  Building2,
  Coins,
  DollarSign,
  TrendingDown,
  TrendingUp,
  Users,
} from "lucide-react";
import {
  useCompanyGrowth,
  useRevenueStats,
  useRevenueTrend,
  useSessionStats,
  useSessionTrend,
  useUserGrowth,
} from "@/features/analytics/api";
import {
  GridBand,
  GridBlock,
  GridCell,
  GridCells,
} from "@/features/analytics/components/GridBlock";
import { gridPageBleedClass } from "@/features/analytics/components/gridPageBleed";
import { StatCard } from "@/features/analytics/components/StatCard";
import { TrendChart } from "@/features/analytics/components/TrendChart";
import { useAuth } from "@/shared/auth/auth-context";
import { formatMoneyMinor } from "@/shared/utils/formatters";
import { last30DaysTrendRange, monthStartIso, nowEndIso, todayStartIso } from "./dateRanges";
import { DashboardCostAlertBanner } from "./components/DashboardCostAlertBanner";

export function SuperAdminDashboardPage() {
  const { user } = useAuth();
  const trendRange = useMemo(() => last30DaysTrendRange(), []);

  const userGrowth = useUserGrowth({});
  const companyGrowth = useCompanyGrowth({});
  const sessionsToday = useSessionStats({
    startDate: todayStartIso(),
    endDate: nowEndIso(),
  });
  const revenueMtd = useRevenueStats({
    startDate: monthStartIso(),
    endDate: nowEndIso(),
  });

  const sessionTrend = useSessionTrend(trendRange);
  const revenueTrend = useRevenueTrend(trendRange);
  const userGrowthTrend = useUserGrowth(trendRange);
  const companyGrowthTrend = useCompanyGrowth(trendRange);

  return (
    <div className={gridPageBleedClass("pb-6")}>
      <GridBlock>
        <GridBand className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="text-xl font-semibold tracking-tight">
              Welcome back{user?.firstName ? `, ${user.firstName}` : ""}
            </h2>
            <p className="mt-1 max-w-xl text-sm text-muted-foreground">
              Platform snapshot — live from admin analytics.
            </p>
          </div>
          <Link
            to="/admin/analytics"
            className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
          >
            Full analytics
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </GridBand>

        <DashboardCostAlertBanner />

        <GridCells className="grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
          <GridCell padded={false}>
            <StatCard
              variant="cell"
              label="Total Users"
              icon={Users}
              tone="brand"
              value={Number(
                userGrowth.data?.totals.total_active ??
                  userGrowth.data?.totals.total_users ??
                  0,
              ).toLocaleString()}
              hint="Active accounts"
              isLoading={userGrowth.isLoading}
            />
          </GridCell>
          <GridCell padded={false}>
            <StatCard
              variant="cell"
              label="Companies"
              icon={Building2}
              tone="info"
              value={Number(
                companyGrowth.data?.totals.total_active ??
                  companyGrowth.data?.totals.total_companies ??
                  0,
              ).toLocaleString()}
              hint="Active"
              isLoading={companyGrowth.isLoading}
            />
          </GridCell>
          <GridCell padded={false}>
            <StatCard
              variant="cell"
              label="Sessions Today"
              icon={Activity}
              tone="success"
              value={Number(
                sessionsToday.data?.total_sessions ?? 0,
              ).toLocaleString()}
              hint="Since midnight"
              isLoading={sessionsToday.isLoading}
            />
          </GridCell>
          <GridCell padded={false}>
            <StatCard
              variant="cell"
              label="Revenue (MTD)"
              icon={DollarSign}
              tone="success"
              value={formatMoneyMinor(
                revenueMtd.data?.total_revenue_cents ?? 0,
                "GBP",
              )}
              hint="Month to date"
              isLoading={revenueMtd.isLoading}
            />
          </GridCell>
          <GridCell padded={false}>
            <StatCard
              variant="cell"
              label="Platform Cost (MTD)"
              icon={Coins}
              tone="warning"
              value={formatMoneyMinor(
                revenueMtd.data?.total_cost_cents ?? 0,
                "GBP",
              )}
              hint="OpenAI / AI pipeline spend"
              isLoading={revenueMtd.isLoading}
            />
          </GridCell>
          <GridCell padded={false}>
            <StatCard
              variant="cell"
              label="Profit (MTD)"
              icon={
                Number(revenueMtd.data?.total_profit_cents ?? 0) >= 0
                  ? TrendingUp
                  : TrendingDown
              }
              tone={
                Number(revenueMtd.data?.total_profit_cents ?? 0) >= 0
                  ? "success"
                  : "warning"
              }
              value={formatMoneyMinor(
                revenueMtd.data?.total_profit_cents ?? 0,
                "GBP",
              )}
              hint="Revenue − cost"
              isLoading={revenueMtd.isLoading}
            />
          </GridCell>
        </GridCells>

        <GridCells className="grid-cols-1 lg:grid-cols-2">
          <GridCell>
            <TrendChart
              variant="cell"
              title="Sessions — last 30 days"
              subtitle="Daily session volume"
              data={sessionTrend.data ?? []}
              isLoading={sessionTrend.isLoading}
              height={240}
              series={[
                {
                  dataKey: "session_count",
                  label: "Sessions",
                  color: "hsl(var(--brand))",
                },
              ]}
            />
          </GridCell>
          <GridCell>
            <TrendChart
              variant="cell"
              title="Revenue — last 30 days"
              subtitle="Daily revenue in GBP"
              data={revenueTrend.data ?? []}
              isLoading={revenueTrend.isLoading}
              height={240}
              series={[
                {
                  dataKey: "total_revenue_cents",
                  label: "Revenue",
                  color: "hsl(var(--success))",
                  format: (v) => formatMoneyMinor(v, "GBP"),
                },
              ]}
            />
          </GridCell>
        </GridCells>

        <GridCells className="grid-cols-1 lg:grid-cols-2">
          <GridCell>
            <TrendChart
              variant="cell"
              title="New users — last 30 days"
              subtitle={
                userGrowthTrend.data
                  ? `${Number(userGrowthTrend.data.totals.total_active ?? 0).toLocaleString()} active total`
                  : undefined
              }
              data={userGrowthTrend.data?.trends ?? []}
              isLoading={userGrowthTrend.isLoading}
              height={220}
              series={[
                {
                  dataKey: "count",
                  label: "Signups",
                  color: "hsl(var(--info))",
                },
              ]}
            />
          </GridCell>
          <GridCell>
            <TrendChart
              variant="cell"
              title="New companies — last 30 days"
              subtitle={
                companyGrowthTrend.data
                  ? `${Number(companyGrowthTrend.data.totals.total_active ?? 0).toLocaleString()} active total`
                  : undefined
              }
              data={companyGrowthTrend.data?.trends ?? []}
              isLoading={companyGrowthTrend.isLoading}
              height={220}
              series={[
                {
                  dataKey: "count",
                  label: "Signups",
                  color: "hsl(var(--warning))",
                },
              ]}
            />
          </GridCell>
        </GridCells>
      </GridBlock>
    </div>
  );
}
